import type { Session, SupabaseClient } from "@supabase/supabase-js";

import "../../styles.css";
import type {
  Contribution,
  LeaderboardItem,
  OAuthProvider,
} from "../shared/api-contract";
import {
  MAX_IMAGE_BYTES,
  SUPPORTED_IMAGE_TYPES,
  trimDatabaseWhitespace,
} from "../shared/api-contract";
import {
  ApiRequestError,
  castCreatedContributionWithReconciliation,
  castExistingContributionWithReconciliation,
  fetchContribution,
  fetchLeaderboard,
  fetchSuggestions,
} from "./api";
import {
  CALLBACK_ATTEMPT_STORAGE_KEY,
  STANDALONE_LOGIN_STORAGE_KEY,
  confirmationActionLabel,
  createBrowserAuthClient,
  handleOAuthCallback,
  launchOAuth,
  launchStandaloneOAuth,
  mountAccountMenu,
  readBrowserAuthConfig,
  removeLegacyContribution,
  type AccountMenuController,
  type OAuthCallbackResult,
} from "./auth";
import {
  OAuthLeaseConflictError,
  PendingContributionStore,
  PendingIntentConflictError,
  type PendingContribution,
} from "./pending-contribution";

type AppView = "list" | "create" | "verify" | "provider" | "auth_status";

type SelectedItem =
  | { kind: "existing"; id: string; name: string; imageUrl: string | null }
  | { kind: "created"; name: string; image: string; imageBlob: Blob };

interface DraftItem {
  name: string;
  image: string;
  imageBlob: Blob;
}

interface CreateDraft {
  name: string | null;
  preparedImage: { blob: Blob; previewUrl: string } | null;
}

interface AuthFlowState {
  intent: PendingContribution;
  status: "choosing" | "completing" | "retryable_error" | "expired";
  message?: string;
}

interface AppState {
  items: LeaderboardItem[];
  total: number;
  matchCount: number;
  leaderboardStatus: "loading" | "ready" | "error";
  leaderboardError: string | null;
  contribution: Contribution | null;
  contributionStatus: "checking" | "ready" | "error";
  contributionError: string | null;
  query: string;
  view: AppView;
  selected: SelectedItem | null;
  session: Session | null;
  authFlow: AuthFlowState | null;
  loginError: string | null;
  createDraft: CreateDraft;
}

const numberFormat = new Intl.NumberFormat("en-US");
const suggestionDebounceMs = 200;
const tabOwnerKey = "the-shit-list.oauth-tab-owner.v1";
const pending = new PendingContributionStore();

const state: AppState = {
  items: [],
  total: 0,
  matchCount: 0,
  leaderboardStatus: "loading",
  leaderboardError: null,
  contribution: null,
  contributionStatus: "checking",
  contributionError: null,
  query: "",
  view: "list",
  selected: null,
  session: null,
  authFlow: null,
  loginError: null,
  createDraft: { name: null, preparedImage: null },
};

const app = queryRequired<HTMLElement>("#app");
const searchForm = queryRequired<HTMLFormElement>("#search-form");
const searchInput = queryRequired<HTMLInputElement>("#search-input");
const suggestionsHost = queryRequired<HTMLElement>("#search-suggestions");
const announcer = queryRequired<HTMLElement>("#announcer");
const accountHost = queryRequired<HTMLElement>("#account-control");
const addItemButton = ensureAddItemButton();
const flowDialog = ensureFlowDialog();
let authClient: SupabaseClient | null = null;
let accountMenu: AccountMenuController | null = null;
let renderedAccountKey: string | null = null;
let leaderboardRequest = 0;
let suggestionRequest = 0;
let suggestionTimer: number | null = null;
let suggestionController: AbortController | null = null;
let suggestionItems: LeaderboardItem[] = [];
let suggestionQuery = "";
let activeSuggestion = -1;
let contributionRequest = 0;
let submissionRetryTimer: number | null = null;
let interactionGeneration = 0;
let createImageSelectionGeneration = 0;
let flowReturnFocus: HTMLElement | null = null;

type LegalDocument = "privacy" | "terms";

const legalDocument = legalDocumentForPath(window.location.pathname);

function clearSubmissionRetryTimer(): void {
  if (submissionRetryTimer === null) return;
  window.clearTimeout(submissionRetryTimer);
  submissionRetryTimer = null;
}

if (legalDocument) {
  renderLegalDocument(legalDocument);
} else {
  discardLegacyContribution();
  searchInput.maxLength = 80;
  render();
  void initializeAuth();
  void loadLeaderboard("");

  searchForm.addEventListener("submit", (event) => {
    event.preventDefault();
    submitSearch(searchInput.value);
  });

  addItemButton.addEventListener("click", openCreateFlow);
  flowDialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    requestDismissFlow();
  });

  searchInput.addEventListener("input", () => scheduleSuggestions(searchInput.value));
  searchInput.addEventListener("focus", () => {
    if (
      suggestionItems.length > 0
      && suggestionQuery === trimDatabaseWhitespace(searchInput.value)
    ) {
      renderSuggestions();
    }
  });
  searchInput.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeSuggestions(true);
      return;
    }
    if (event.key === "ArrowDown" && suggestionItems.length > 0) {
      event.preventDefault();
      setActiveSuggestion((activeSuggestion + 1) % suggestionItems.length);
      return;
    }
    if (event.key === "ArrowUp" && suggestionItems.length > 0) {
      event.preventDefault();
      setActiveSuggestion(
        activeSuggestion <= 0 ? suggestionItems.length - 1 : activeSuggestion - 1,
      );
      return;
    }
    if (event.key === "Enter" && activeSuggestion >= 0) {
      event.preventDefault();
      const item = suggestionItems[activeSuggestion];
      if (item) chooseSuggestion(item);
    }
  });

  document.addEventListener("pointerdown", (event) => {
    if (event.target instanceof Node && !searchForm.contains(event.target)) closeSuggestions(true);
  });
  document.addEventListener("focusin", (event) => {
    if (event.target instanceof Node && !searchForm.contains(event.target)) closeSuggestions(true);
  });
}

function legalDocumentForPath(pathname: string): LegalDocument | null {
  if (pathname === "/privacy" || pathname === "/privacy/") return "privacy";
  if (pathname === "/terms" || pathname === "/terms/") return "terms";
  return null;
}

function renderLegalDocument(documentKind: LegalDocument): void {
  document.body.classList.add("legal-route");
  document.title = documentKind === "privacy"
    ? "Privacy Policy — The Shit List"
    : "Terms of Service — The Shit List";
  const article = element("article", "legal-document");
  const eyebrow = element("p", "legal-document__eyebrow", "THE SHIT LIST");
  const heading = element(
    "h1",
    "legal-document__title",
    documentKind === "privacy" ? "Privacy Policy" : "Terms of Service",
  );
  const updated = element("p", "legal-document__updated", "Effective September 2, 2026");
  article.append(eyebrow, heading, updated);

  if (documentKind === "privacy") appendPrivacyPolicy(article);
  else appendTermsOfService(article);

  const home = element("a", "button button--outline legal-document__home", "Back to the list");
  home.href = "/";
  article.append(home);
  app.replaceChildren(article);
  app.focus({ preventScroll: true });
}

function appendPrivacyPolicy(article: HTMLElement): void {
  appendLegalSection(article, "The short version", [
    "Google sign-in is handled by Supabase Auth. Our voting database does not store your Google email, name, profile photo, or Supabase user UUID. It stores a keyed, pseudonymous 32-byte code so the same account cannot contribute twice.",
    "Item names, item images, and total vote counts are public. The site does not publicly reveal which account contributed to which item. We do not sell personal information, run behavioral advertising, or use advertising trackers.",
  ]);
  appendLegalSection(article, "Information processed when you sign in", [
    "Google supplies basic sign-in information to Supabase, which may include your Google account identifier, email address, name, and profile image. Supabase stores and manages the authentication account and session. The site requests only the standard OpenID, email, and profile information needed to sign you in; it does not request access to Gmail, Google Drive, Calendar, contacts, or other Google services.",
    "The Cloudflare Worker verifies the signed Supabase access token and checks that its Supabase session is active. It briefly uses the Supabase user UUID to calculate an application-scoped HMAC identity key, then uses only that key for the voting ledger. The raw UUID and Google profile fields are not written to application tables.",
  ]);
  appendLegalSection(article, "Contribution and technical information", [
    "For a contribution, we process the item you select, a request identifier, the contribution time, and a technical fingerprint of the request used to make retries safe. If you create an item, its name and submitted image are also processed and published.",
    "Cloudflare and Supabase necessarily process ordinary network and request information to deliver and secure the service. Our application logs are designed to contain route names, response status, timing, and broad failure categories—not access tokens, emails, Supabase user UUIDs, profile data, or uploaded image bytes.",
    "Your browser stores the Supabase sign-in session. If you begin a contribution, the pending item and any selected image may be held temporarily in IndexedDB so the contribution can survive the Google redirect. Short-lived OAuth attempt and tab-coordination values may be held in local or session storage.",
  ]);
  appendLegalSection(article, "How we use information", [], [
    "Authenticate you and confirm that your session is active.",
    "Enforce one permanent contribution per signed-in account.",
    "Publish and rank the items submitted to the list.",
    "Prevent abuse, safely retry interrupted submissions, and operate the service.",
  ]);
  appendLegalSection(article, "What is public", [
    "Item names and images are public, as are aggregate vote totals and rankings. The private vote ledger connects an item only to the pseudonymous voter key; that association is not returned by the public API.",
  ]);
  appendLegalSection(article, "Service providers", [
    "We use Google for sign-in, Supabase for authentication and PostgreSQL hosting, and Cloudflare for the website, Worker backend, rate limiting, and image storage. Each provider processes information under its own terms and privacy commitments. We do not give these providers permission to use your information for our advertising because the service has no advertising program.",
  ]);
  appendLegalSection(article, "Retention and your choices", [
    "The pseudonymous voter key, selected item, safe-retry fields, and contribution time are retained as part of the permanent voting ledger. This is necessary to preserve the result and prevent the same account from contributing again. Deleting or disconnecting the Google or Supabase account does not automatically delete that historical ledger entry or restore voting eligibility.",
    "Published items and images are kept while they remain part of the service, unless removed for safety, legal, or moderation reasons. Authentication records and infrastructure logs are retained according to Supabase and Cloudflare settings and operational needs. Browser-held pending contribution data is removed after completion, cancellation, or expiry.",
    "You can log out using the Account menu and can revoke the app's Google access from your Google Account. The service does not currently provide a way to change or withdraw a completed contribution.",
  ]);
  appendLegalSection(article, "Security", [
    "We use access controls, signed-token verification, an isolated private database schema, encrypted connections, restricted runtime credentials, and a secret-keyed voter identifier. No online service can guarantee absolute security.",
  ]);
  appendLegalSection(article, "Children", [
    "The service is not directed to children under 13, and you must be at least 13 to use it. If you believe a child has provided personal information through the service, contact us using the support email shown on the Google sign-in consent screen.",
  ]);
  appendLegalSection(article, "Changes and contact", [
    "We may update this policy as the service changes. The effective date at the top will be revised when we do. Privacy questions can be sent to the user-support email displayed on the Google sign-in consent screen.",
  ]);
}

function appendTermsOfService(article: HTMLElement): void {
  appendLegalSection(article, "Agreement and eligibility", [
    "By accessing or using The Shit List, you agree to these Terms. If you do not agree, do not use the service. You must be at least 13 years old and legally able to enter this agreement.",
  ]);
  appendLegalSection(article, "How the service works", [
    "The Shit List is a public, opinion-based leaderboard. A signed-in account may make one permanent contribution by choosing an existing item or creating a new item with a name and image. A contribution cannot be changed, transferred, or withdrawn through the service after it is confirmed.",
    "One account is not necessarily one physical person. We may use reasonable technical and moderation controls to protect the list, but we do not promise that every participant is unique.",
  ]);
  appendLegalSection(article, "Accounts", [
    "Google sign-in is provided through Supabase Auth. You are responsible for your Google account and for activity performed through it. Do not attempt to bypass the one-contribution limit, interfere with authentication, probe private systems, or disrupt the service.",
  ]);
  appendLegalSection(article, "Items and images you submit", [
    "You keep ownership of content you submit. You give us a non-exclusive, worldwide, royalty-free license to host, process, resize, display, and distribute it only as needed to operate, secure, and promote the service.",
    "You may submit content only when you have the right to use it. Do not submit illegal content; threats; targeted harassment; private personal information; sexual content involving minors; malware; impersonation; defamatory factual allegations; or content that infringes copyright, trademark, privacy, publicity, or other rights. Do not use the service to target private individuals. Entries express contributors' opinions and must not be presented as verified factual claims.",
  ]);
  appendLegalSection(article, "Moderation", [
    "We may reject, hide, quarantine, or remove content and may restrict access when we reasonably believe it violates these Terms, creates legal or safety risk, abuses the service, or threatens reliable operation. Removing an item does not obligate us to let an account contribute again.",
  ]);
  appendLegalSection(article, "Availability and changes", [
    "We may change, suspend, or discontinue any part of the service. We do not promise uninterrupted availability, preservation of every item or image, or any particular ranking or audience size.",
  ]);
  appendLegalSection(article, "Disclaimers", [
    "The service and all user-submitted content are provided “as is” and “as available.” To the extent permitted by law, we disclaim warranties of merchantability, fitness for a particular purpose, non-infringement, accuracy, and availability. Rankings and entries are opinions and are not professional advice or verified statements of fact.",
  ]);
  appendLegalSection(article, "Limitation of liability", [
    "To the extent permitted by law, The Shit List and its operator will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, lost data, lost profits, reputational harm, or damages arising from user-submitted content or inability to use the service. Rights that cannot legally be waived remain unaffected.",
  ]);
  appendLegalSection(article, "Changes and contact", [
    "We may update these Terms as the service changes. Continued use after an updated effective date means you accept the revised Terms. Questions can be sent to the user-support email displayed on the Google sign-in consent screen.",
  ]);
}

function appendLegalSection(
  article: HTMLElement,
  heading: string,
  paragraphs: readonly string[],
  bullets: readonly string[] = [],
): void {
  const section = element("section", "legal-section");
  section.append(element("h2", "", heading));
  paragraphs.forEach((paragraph) => section.append(element("p", "", paragraph)));
  if (bullets.length > 0) {
    const list = element("ul");
    bullets.forEach((bullet) => list.append(element("li", "", bullet)));
    section.append(list);
  }
  article.append(section);
}

function submitSearch(query: string): void {
  closeSuggestions(true);
  interactionGeneration += 1;
  flowReturnFocus = null;
  clearCreateDraft();
  state.view = "list";
  replaceSelected(null);
  state.authFlow = null;
  void loadLeaderboard(query);
  app.focus({ preventScroll: true });
}

function scheduleSuggestions(value: string): void {
  cancelSuggestionWork();
  suggestionItems = [];
  activeSuggestion = -1;
  renderSuggestions();
  const query = trimDatabaseWhitespace(value);
  if (query === "") return;
  suggestionTimer = window.setTimeout(() => {
    suggestionTimer = null;
    void loadSuggestions(query);
  }, suggestionDebounceMs);
}

async function loadSuggestions(query: string): Promise<void> {
  const request = ++suggestionRequest;
  const controller = new AbortController();
  suggestionController = controller;
  suggestionQuery = query;
  try {
    const response = await fetchSuggestions(query, fetch, controller.signal);
    if (
      request !== suggestionRequest
      || controller.signal.aborted
      || trimDatabaseWhitespace(searchInput.value) !== query
    ) return;
    suggestionItems = response.items;
    activeSuggestion = -1;
    renderSuggestions();
    if (suggestionItems.length > 0) {
      announce(
        `${suggestionItems.length} close ${suggestionItems.length === 1 ? "match" : "matches"} available.`,
      );
    }
  } catch (_error) {
    if (request !== suggestionRequest || controller.signal.aborted) return;
    suggestionItems = [];
    renderSuggestions();
  } finally {
    if (suggestionController === controller) suggestionController = null;
  }
}

function chooseSuggestion(item: LeaderboardItem): void {
  searchInput.value = item.name;
  submitSearch(item.name);
}

function setActiveSuggestion(index: number): void {
  activeSuggestion = index;
  renderSuggestions();
}

function renderSuggestions(): void {
  suggestionsHost.replaceChildren();
  const open = suggestionItems.length > 0;
  suggestionsHost.hidden = !open;
  searchInput.setAttribute("aria-expanded", String(open));
  if (!open) {
    searchInput.removeAttribute("aria-activedescendant");
    return;
  }

  suggestionItems.forEach((item, index) => {
    const option = element("button", "search-suggestion");
    option.id = `search-suggestion-${index}`;
    option.type = "button";
    option.tabIndex = -1;
    option.setAttribute("role", "option");
    option.setAttribute("aria-selected", String(index === activeSuggestion));
    if (index === activeSuggestion) option.classList.add("is-active");
    option.append(
      element("span", "search-suggestion__name", item.name),
      element(
        "span",
        "search-suggestion__meta",
        `#${item.rank} · ${numberFormat.format(item.voteCount)} ${item.voteCount === 1 ? "vote" : "votes"}`,
      ),
    );
    option.addEventListener("pointerdown", (event) => event.preventDefault());
    option.addEventListener("click", () => chooseSuggestion(item));
    suggestionsHost.append(option);
  });

  if (activeSuggestion >= 0) {
    searchInput.setAttribute("aria-activedescendant", `search-suggestion-${activeSuggestion}`);
  } else {
    searchInput.removeAttribute("aria-activedescendant");
  }
}

function closeSuggestions(cancelPending = false): void {
  if (cancelPending) cancelSuggestionWork();
  suggestionItems = [];
  activeSuggestion = -1;
  renderSuggestions();
}

function cancelSuggestionWork(): void {
  if (suggestionTimer !== null) {
    window.clearTimeout(suggestionTimer);
    suggestionTimer = null;
  }
  suggestionController?.abort();
  suggestionController = null;
  suggestionRequest += 1;
}

async function initializeAuth(): Promise<void> {
  let callbackResult: OAuthCallbackResult = { status: "idle" };
  try {
    const config = readBrowserAuthConfig(import.meta.env);
    authClient = createBrowserAuthClient(config);
    if (window.location.pathname === "/auth/callback") {
      callbackResult = await handleOAuthCallback({
        auth: authClient.auth,
        href: window.location.href,
        history: window.history,
        storage: window.localStorage,
        pending,
      });
      applyCallbackResult(callbackResult);
    }

    const { data } = await authClient.auth.getSession();
    state.session = data.session;
    await refreshContribution(data.session);
    if (callbackResult.status === "idle") await restoreUnfinishedIntent();
    render();
    if (state.authFlow?.status === "completing") {
      await completeActiveIntent();
    }
    authClient.auth.onAuthStateChange((event, session) => {
      queueMicrotask(() => {
        if (
          event === "INITIAL_SESSION"
          && state.session?.access_token === session?.access_token
        ) return;
        const signedInChanged = Boolean(state.session) !== Boolean(session);
        state.session = session;
        if (signedInChanged) {
          if (session) state.loginError = null;
          renderAccountControl();
          render();
        }
        void refreshContribution(session).then(() => {
          if (state.authFlow?.status === "completing") void completeActiveIntent();
        });
      });
    });
    render();
  } catch (_error) {
    state.session = null;
    state.contribution = null;
    state.contributionStatus = "ready";
    state.contributionError = null;
    if (callbackResult.status === "idle") await restoreUnfinishedIntent();
    render();
  }
}

async function loadLeaderboard(query: string): Promise<void> {
  const request = ++leaderboardRequest;
  state.query = trimDatabaseWhitespace(query);
  state.leaderboardStatus = "loading";
  state.leaderboardError = null;
  state.items = [];
  state.total = 0;
  state.matchCount = 0;
  render();
  try {
    const response = await fetchLeaderboard(state.query);
    if (request !== leaderboardRequest) return;
    state.items = response.items;
    state.total = response.totalVotes;
    state.matchCount = response.matchCount;
    state.leaderboardStatus = "ready";
  } catch (error) {
    if (request !== leaderboardRequest) return;
    state.leaderboardStatus = "error";
    state.leaderboardError = error instanceof Error
      ? error.message
      : "The leaderboard is temporarily unavailable. Try again.";
  }
  render();
}

async function refreshContribution(session: Session | null): Promise<void> {
  const request = ++contributionRequest;
  state.contribution = null;
  state.contributionError = null;
  if (session === null) {
    state.contributionStatus = "ready";
    render();
    return;
  }

  state.contributionStatus = "checking";
  render();
  try {
    const contribution = await fetchContribution(session.access_token);
    if (request !== contributionRequest) return;
    state.contribution = contribution;
    state.contributionStatus = "ready";
  } catch (error) {
    if (request !== contributionRequest) return;
    state.contributionStatus = "error";
    state.contributionError = error instanceof ApiRequestError && error.status === 401
      ? "Your sign-in is no longer active. Log out, then sign in again."
      : "Your contribution status could not be checked. Try again before casting.";
  }
  render();
}

function applyCallbackResult(result: OAuthCallbackResult): void {
  if (result.status === "standalone_complete") {
    state.loginError = null;
    state.view = "list";
    announce("You are logged in.");
    return;
  }
  if (result.status === "standalone_error") {
    state.loginError = result.message;
    state.view = "list";
    return;
  }
  if (result.status === "idle" || result.status === "canceled" || !result.intent) return;
  replaceSelected(selectedFromIntent(result.intent));
  state.authFlow = {
    intent: result.intent,
    status: result.status,
    message: "message" in result ? result.message : undefined,
  };
  state.view = "auth_status";
}

async function restoreUnfinishedIntent(): Promise<void> {
  const intent = await pending.getUnfinished();
  if (!intent) return;
  replaceSelected(selectedFromIntent(intent));
  if (intent.status === "confirmed") {
    state.authFlow = { intent, status: "choosing" };
    state.view = "provider";
    return;
  }
  if (intent.status === "completing") {
    state.authFlow = { intent, status: "completing" };
    state.view = "auth_status";
    return;
  }
  state.authFlow = {
    intent,
    status: intent.status === "expired" ? "expired" : "retryable_error",
    message: "This sign-in needs attention. Try again or cancel this contribution.",
  };
  state.view = "auth_status";
}

function render(): void {
  app.replaceChildren();
  renderAccountControl();
  renderAddItemControl();
  renderContributionStatus();
  if (state.leaderboardStatus === "loading") {
    renderListStatus("Loading the leaderboard…", false);
  } else if (state.leaderboardStatus === "error") {
    renderListStatus(
      state.leaderboardError ?? "The leaderboard is temporarily unavailable. Try again.",
      true,
    );
  } else if (state.items.length === 0) {
    if (!state.query) renderEmptyCatalog();
    else renderNoMatches();
  } else {
    renderLeaderboard(state.items);
  }
  renderFlowDialog();
}

function canContribute(): boolean {
  return state.contributionStatus === "ready" && state.contribution === null;
}

function renderContributionStatus(): void {
  if (state.contributionStatus === "checking") {
    const status = element("p", "status-banner", "Checking your contribution status…");
    status.setAttribute("role", "status");
    app.append(status);
    return;
  }
  if (state.contributionStatus === "error") {
    const status = element(
      "p",
      "status-banner form-error",
      state.contributionError ?? "Your contribution status is unavailable.",
    );
    status.setAttribute("role", "alert");
    app.append(status);
    return;
  }
  if (state.contribution !== null) {
    const status = element("div", "status-banner");
    const copy = element("p");
    copy.append(
      element("strong", "", "Your permanent contribution: "),
      document.createTextNode(state.contribution.item.name),
    );
    status.append(copy);
    app.append(status);
  }
}

function renderListStatus(message: string, retryable: boolean): void {
  const section = element("section", "empty-state");
  const heading = element("h1", "state-heading", retryable ? "Couldn’t load the list" : message);
  section.append(heading);
  if (retryable) {
    const detail = element("p", "form-error", message);
    detail.setAttribute("role", "alert");
    const retry = element("button", "button button--outline", "Try again");
    retry.type = "button";
    retry.addEventListener("click", () => void loadLeaderboard(state.query));
    section.append(detail, retry);
  } else {
    heading.setAttribute("role", "status");
  }
  app.append(section);
}

function renderEmptyCatalog(): void {
  const section = element("section", "empty-state empty-catalog");
  section.append(
    element("h1", "state-heading", "The list is empty."),
    element(
      "p",
      "empty-state__copy",
      "No entries yet. Use Add item above when you’re ready to make the first permanent contribution.",
    ),
  );
  app.append(section);
}

function renderNoMatches(): void {
  const section = element("section", "empty-state no-matches");
  const heading = element("h1", "state-heading", "No matches.");
  section.append(
    heading,
    element(
      "p",
      "empty-state__copy",
      `Nothing on the list matches “${state.query}”. Use Add item above if it belongs here.`,
    ),
  );
  app.append(section);
}

function renderAddItemControl(): void {
  addItemButton.disabled = !canContribute();
  if (state.contributionStatus === "checking") {
    addItemButton.title = "Checking contribution eligibility";
  } else if (state.contributionStatus === "error") {
    addItemButton.title = "Contribution eligibility is unavailable";
  } else if (state.contribution !== null) {
    addItemButton.title = "This account already made its permanent contribution";
  } else {
    addItemButton.removeAttribute("title");
  }
}

function renderAccountControl(): void {
  const signedIn = Boolean(state.session);
  const accountKey = `${signedIn}:${state.loginError ?? ""}`;
  if (renderedAccountKey === accountKey) return;
  renderedAccountKey = accountKey;
  accountMenu?.destroy();
  accountMenu = mountAccountMenu(accountHost, {
    signedIn,
    loginError: state.loginError,
    onLogin: (button) => startStandaloneProvider("google", button),
    onLogout: logout,
  });
  if (!signedIn && state.loginError) accountMenu.open();
}

function renderLeaderboard(items: LeaderboardItem[]): void {
  const header = element("div", "section-header");
  header.append(
    element("h1", "", "Leaderboard"),
    element(
      "p",
      "total-count",
      state.query
        ? `${state.matchCount} ${state.matchCount === 1 ? "match" : "matches"}`
        : `${numberFormat.format(state.total)} permanent votes`,
    ),
  );
  const list = element("ol", "leaderboard");
  list.setAttribute("aria-label", "Leaderboard");
  items.forEach((item) => list.append(buildLeaderboardRow(item)));
  app.append(header, list);
}

function buildLeaderboardRow(item: LeaderboardItem): HTMLLIElement {
  const row = element("li", "leaderboard-row");
  const rankNode = element("span", "rank", String(item.rank));
  rankNode.setAttribute("aria-label", `Rank ${item.rank}`);
  const image = leaderboardImage(item.imageUrl, item.rank);
  const name = element("h2", "item-name", item.name);
  const count = element("span", "vote-count", numberFormat.format(item.voteCount));
  count.setAttribute("aria-label", `${numberFormat.format(item.voteCount)} votes`);
  const action = element(
    "button",
    "button button--outline",
    state.contribution !== null
      ? "Already cast"
      : state.contributionStatus === "checking"
        ? "Checking eligibility…"
        : state.contributionStatus === "error"
          ? "Eligibility unavailable"
          : "Cast hatred",
  );
  action.type = "button";
  action.setAttribute("aria-label", `Cast my contribution for ${item.name}`);
  action.disabled = !canContribute();
  action.addEventListener("click", () => beginVerification(
    {
      kind: "existing",
      id: item.id,
      name: item.name,
      imageUrl: item.imageUrl,
    },
    action,
  ));
  row.append(rankNode, image, name, count, action);
  return row;
}

function leaderboardImage(imageUrl: string | null, rank: number): HTMLElement {
  if (imageUrl === null) {
    const placeholder = element("span", "item-image");
    placeholder.setAttribute("aria-hidden", "true");
    return placeholder;
  }
  const image = document.createElement("img");
  image.className = "item-image";
  image.src = imageUrl;
  image.alt = "";
  image.width = 640;
  image.height = 480;
  image.loading = rank > 4 ? "lazy" : "eager";
  return image;
}

function renderFlowDialog(): void {
  if (state.view === "list") {
    if (flowDialog.open) flowDialog.close();
    flowDialog.replaceChildren();
    return;
  }

  flowDialog.replaceChildren();
  openFlowDialog();
  if (state.view === "create") renderCreate();
  else if (state.view === "verify") renderVerification();
  else if (state.view === "provider") renderProviderChooser();
  else renderAuthStatus();
}

function openFlowDialog(): void {
  if (flowDialog.open) return;
  try {
    flowDialog.showModal();
  } catch (_error) {
    flowDialog.setAttribute("open", "");
  }
}

function appendFlowHeader(section: HTMLElement, title: string, closable = true): void {
  const header = element("div", "flow-dialog__header");
  const heading = element("h1", "state-heading", title);
  heading.id = "flow-title";
  header.append(heading);
  if (closable) {
    const close = element("button", "flow-dialog__close", "Close");
    close.type = "button";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", requestDismissFlow);
    header.append(close);
  }
  section.append(header);
}

function renderCreate(): void {
  const section = element("section", "flow-state create-flow");
  appendFlowHeader(section, "Add an item");
  section.append(
    element(
      "p",
      "flow-intro",
      "Use a clear name and image. You’ll review the exact item before anything becomes permanent.",
    ),
  );
  const form = element("form", "create-form");
  form.noValidate = true;
  const nameLabel = element("label", "field-label", "Name");
  nameLabel.htmlFor = "item-name";
  const nameStack = element("div", "field-stack");
  const nameInput = document.createElement("input");
  nameInput.id = "item-name";
  nameInput.name = "name";
  nameInput.type = "text";
  nameInput.maxLength = 80;
  nameInput.required = true;
  state.createDraft.name ??= state.query;
  nameInput.value = state.createDraft.name;
  nameStack.append(nameInput);
  const imageLabel = element("span", "field-label", "Image");
  const imageStack = element("div", "field-stack");
  const drop = element("label", "file-drop");
  drop.htmlFor = "item-image";
  const fileInput = document.createElement("input");
  fileInput.id = "item-image";
  fileInput.name = "image";
  fileInput.type = "file";
  fileInput.accept = "image/jpeg,image/png,image/webp";
  fileInput.required = true;
  const instructions = element("span", "file-instructions");
  instructions.append(
    element("strong", "", "Choose an image"),
    element("small", "", "JPG, PNG or WebP · 5 MB max"),
  );
  const preview = document.createElement("img");
  preview.className = "image-preview";
  preview.alt = "Selected item preview";
  drop.append(fileInput, instructions, preview);
  const error = element("p", "form-error");
  error.id = "image-error";
  error.setAttribute("role", "alert");
  imageStack.append(drop, error);
  const restoredImage = state.createDraft.preparedImage;
  if (restoredImage) {
    preview.src = restoredImage.previewUrl;
    drop.classList.add("has-preview");
  }
  let imageBusy = false;
  const actions = element("div", "form-actions");
  const continueButton = element("button", "button button--primary", "Continue");
  continueButton.type = "submit";
  continueButton.disabled = !restoredImage || !nameInput.value.trim();
  actions.append(
    continueButton,
    textButton("Cancel", requestDismissFlow),
  );
  form.append(nameLabel, nameStack, imageLabel, imageStack, actions);
  section.append(form);
  flowDialog.append(section);

  fileInput.addEventListener("change", async () => {
    const selectionGeneration = ++createImageSelectionGeneration;
    const file = fileInput.files?.[0];
    if (!file) return;
    error.textContent = "";
    imageBusy = true;
    continueButton.disabled = true;
    try {
      validateImage(file);
      if (state.createDraft.preparedImage) {
        URL.revokeObjectURL(state.createDraft.preparedImage.previewUrl);
        state.createDraft.preparedImage = null;
      }
      const compressedImage = await compressImage(file);
      if (selectionGeneration !== createImageSelectionGeneration) {
        URL.revokeObjectURL(compressedImage.previewUrl);
        return;
      }
      state.createDraft.preparedImage = compressedImage;
      if (form.isConnected) {
        preview.src = compressedImage.previewUrl;
        drop.classList.add("has-preview");
      }
    } catch (caught) {
      if (selectionGeneration !== createImageSelectionGeneration) return;
      state.createDraft.preparedImage = null;
      drop.classList.remove("has-preview");
      error.textContent = caught instanceof Error ? caught.message : "Choose a valid image.";
      fileInput.value = "";
    } finally {
      if (selectionGeneration !== createImageSelectionGeneration) return;
      imageBusy = false;
      if (!form.isConnected) {
        if (document.querySelector(".create-form")) render();
        return;
      }
      continueButton.disabled = !state.createDraft.preparedImage || !nameInput.value.trim();
    }
  });
  nameInput.addEventListener("input", () => {
    state.createDraft.name = nameInput.value;
    continueButton.disabled = imageBusy || !state.createDraft.preparedImage || !nameInput.value.trim();
  });
  ["dragenter", "dragover"].forEach((eventName) =>
    drop.addEventListener(eventName, () => drop.classList.add("is-dragging")),
  );
  ["dragleave", "drop"].forEach((eventName) =>
    drop.addEventListener(eventName, () => drop.classList.remove("is-dragging")),
  );
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const itemName = nameInput.value.trim();
    const preparedImage = state.createDraft.preparedImage;
    if (!itemName || !preparedImage) return;
    const draft: DraftItem = {
      name: itemName,
      image: preparedImage.previewUrl,
      imageBlob: preparedImage.blob,
    };
    createImageSelectionGeneration += 1;
    state.createDraft = { name: null, preparedImage: null };
    beginVerification({ ...draft, kind: "created" });
  });
  nameInput.focus();
}

function renderVerification(): void {
  const item = state.selected;
  if (!item) return resetToList();
  const section = element("section", "flow-state verification");
  appendFlowHeader(section, "Make it permanent");
  section.append(selectedItemSummary(item));
  const actions = element("div", "final-actions");
  const confirm = element(
    "button",
    "button button--primary",
    confirmationActionLabel(Boolean(state.session)),
  );
  confirm.type = "button";
  confirm.addEventListener("click", () => confirmContribution(confirm));
  actions.append(confirm, textButton("Cancel", requestDismissFlow));
  section.append(actions, element("p", "final-warning", "This cannot be changed or undone."));
  flowDialog.append(section);
  confirm.focus();
}

function renderProviderChooser(): void {
  const flow = state.authFlow;
  const item = state.selected;
  if (!flow || !item) return resetToList();
  const section = element("section", "flow-state auth-flow");
  appendFlowHeader(section, "Sign in and cast");
  section.append(selectedItemSummary(item));
  section.append(
    element("p", "auth-flow__copy", "Continue with Google to cast this exact contribution."),
  );
  const actions = element("div", "provider-actions");
  const google = element("button", "button button--primary", "Continue with Google");
  google.type = "button";
  google.addEventListener("click", () => startProvider("google", google));
  actions.append(google, textButton("Cancel", () => void cancelPendingIntent()));
  section.append(actions, element("p", "final-warning", "This cannot be changed or undone."));
  flowDialog.append(section);
  google.focus();
}

async function startStandaloneProvider(
  provider: OAuthProvider,
  selectedButton: HTMLButtonElement,
): Promise<void> {
  if (!authClient) {
    state.loginError = "Login is not configured. Try again later.";
    render();
    return;
  }
  selectedButton.disabled = true;
  selectedButton.setAttribute("aria-busy", "true");
  try {
    await launchStandaloneOAuth({
      auth: authClient.auth,
      provider,
      origin: window.location.origin,
      storage: window.localStorage,
    });
  } catch (_error) {
    state.loginError = "Login could not be started. Try again.";
    render();
  }
}

function renderAuthStatus(): void {
  const flow = state.authFlow;
  if (!flow) return resetToList();
  const section = element("section", "flow-state auth-flow");
  const completing = flow.status === "completing";
  appendFlowHeader(
    section,
    completing ? "Completing your contribution" : "Sign-in needs attention",
    !completing,
  );
  if (state.selected) section.append(selectedItemSummary(state.selected));
  const status = element(
    "p",
    completing ? "auth-flow__status" : "form-error auth-flow__status",
    completing
      ? "You’re signed in. Your exact confirmed contribution is being cast securely."
      : flow.message ?? "Try sign-in again or cancel this contribution.",
  );
  status.setAttribute("role", completing ? "status" : "alert");
  status.setAttribute("aria-live", completing ? "polite" : "assertive");
  section.append(status);
  const actions = element("div", "final-actions");
  if (!completing) {
    actions.append(
      textButton("Try again", () => void retryPendingIntent()),
      textButton("Cancel", () => void cancelPendingIntent()),
    );
  }
  section.append(actions);
  flowDialog.append(section);
  actions.querySelector<HTMLElement>("button")?.focus();
}

function selectedItemSummary(item: SelectedItem): HTMLElement {
  const summary = element("div", "selected-item");
  const imageUrl = item.kind === "created" ? item.image : item.imageUrl;
  const image = imageUrl === null
    ? element("span", "item-image")
    : document.createElement("img");
  if (image instanceof HTMLImageElement) {
    image.src = imageUrl!;
    image.alt = "";
  } else {
    image.setAttribute("aria-hidden", "true");
  }
  const copy = element("div");
  copy.append(
    element("h2", "", item.name),
    element("p", "", "This exact item will be permanently bound to your signed-in account."),
  );
  summary.append(image, copy);
  return summary;
}

function openCreateFlow(): void {
  if (!canContribute()) {
    announce("Your contribution status must be available before you can add an item.");
    return;
  }
  closeSuggestions(true);
  flowReturnFocus = addItemButton;
  state.createDraft.name ??= state.query || null;
  state.view = "create";
  replaceSelected(null);
  state.authFlow = null;
  render();
}

function beginVerification(item: SelectedItem, trigger?: HTMLElement): void {
  if (!canContribute()) {
    announce("Your contribution status must be available before you can cast.");
    return;
  }
  if (trigger) flowReturnFocus = trigger;
  replaceSelected(item);
  state.view = "verify";
  render();
}

function requestDismissFlow(): void {
  if (state.authFlow) {
    if (state.authFlow.status === "completing") {
      announce("Your contribution is still being completed.");
      return;
    }
    void cancelPendingIntent();
    return;
  }
  resetToList();
}

async function confirmContribution(button: HTMLButtonElement): Promise<void> {
  const selected = state.selected;
  if (!selected || !canContribute()) return;
  const generation = interactionGeneration;
  let createdIntent: PendingContribution | null = null;
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  try {
    const intent = await pending.create(
      selected.kind === "created"
        ? { kind: "created", name: selected.name, image: requiredCreatedImage(selected) }
        : {
            kind: "existing",
            itemId: selected.id,
            name: selected.name,
            imageUrl: selected.imageUrl,
          },
    );
    createdIntent = intent;
    if (generation !== interactionGeneration) {
      await pending.cancel(intent.intentId);
      return;
    }
    const session = state.session;
    const activeIntent = session
      ? await pending.markCompletingWithoutOAuth(intent.intentId)
      : intent;
    if (generation !== interactionGeneration) {
      await pending.cancel(intent.intentId);
      return;
    }
    state.authFlow = { intent: activeIntent, status: session ? "completing" : "choosing" };
    state.view = session ? "auth_status" : "provider";
    render();
    if (session) void completeActiveIntent();
  } catch (error) {
    if (generation !== interactionGeneration) {
      if (createdIntent) await pending.cancel(createdIntent.intentId);
      return;
    }
    const existing = await pending.getUnfinished();
    if (generation !== interactionGeneration) {
      if (createdIntent) await pending.cancel(createdIntent.intentId);
      return;
    }
    if (error instanceof PendingIntentConflictError && existing) {
      state.authFlow = { intent: existing, status: "retryable_error", message: error.message };
      replaceSelected(selectedFromIntent(existing));
      state.view = "auth_status";
      render();
      return;
    }
    button.disabled = false;
    button.removeAttribute("aria-busy");
    announce(error instanceof Error ? error.message : "The contribution could not be prepared.");
  }
}

async function startProvider(
  provider: OAuthProvider,
  selectedButton: HTMLButtonElement,
): Promise<void> {
  const flow = state.authFlow;
  if (!flow || !authClient) {
    if (flow) {
      flow.status = "retryable_error";
      flow.message = "Sign-in is not configured. Try again later or cancel.";
      state.view = "auth_status";
      render();
    }
    return;
  }
  selectedButton.disabled = true;
  selectedButton.setAttribute("aria-busy", "true");
  try {
    await launchOAuth({
      auth: authClient.auth,
      provider,
      intentId: flow.intent.intentId,
      ownerId: tabOwnerId(),
      origin: window.location.origin,
      storage: window.localStorage,
      pending,
    });
  } catch (error) {
    flow.status = "retryable_error";
    flow.message =
      error instanceof OAuthLeaseConflictError
        ? error.message
        : "Sign-in could not be started. Try again or cancel this contribution.";
    state.view = "auth_status";
    render();
  }
}

async function retryPendingIntent(): Promise<void> {
  const flow = state.authFlow;
  if (!flow) return;
  if (state.session && flow.status !== "expired") {
    flow.status = "completing";
    delete flow.message;
    state.view = "auth_status";
    render();
    void completeActiveIntent();
    return;
  }
  if (flow.status === "expired") {
    const target = flow.intent.target;
    await pending.cancel(flow.intent.intentId);
    const replacement = await pending.create(target);
    state.authFlow = { intent: replacement, status: "choosing" };
  } else {
    await pending.markRetryable(flow.intent.intentId, "user_retry");
    state.authFlow = { intent: { ...flow.intent, status: "retryable_error" }, status: "choosing" };
  }
  state.view = "provider";
  render();
}

async function completeActiveIntent(): Promise<void> {
  const flow = state.authFlow;
  const session = state.session;
  if (!flow || !session) return;
  const ownerId = tabOwnerId();

  try {
    const lease = await pending.acquireSubmissionLease(flow.intent.intentId, ownerId);
    if (!lease.acquired) {
      clearSubmissionRetryTimer();
      submissionRetryTimer = window.setTimeout(() => {
        submissionRetryTimer = null;
        void completeActiveIntent();
      }, Math.max(25, lease.retryAfterMs + 25));
      return;
    }

    const renewal = pending.startSubmissionLeaseRenewal(flow.intent.intentId, ownerId);
    const result = await (async () => {
      try {
        const target = flow.intent.target;
        return state.contribution !== null
          ? { outcome: "already_cast" as const, contribution: state.contribution }
          : target.kind === "existing"
            ? await castExistingContributionWithReconciliation({
                accessToken: session.access_token,
                itemId: target.itemId,
                idempotencyKey: flow.intent.idempotencyKey,
              })
            : await castCreatedContributionWithReconciliation({
                accessToken: session.access_token,
                name: target.name,
                image: target.image,
                idempotencyKey: flow.intent.idempotencyKey,
              });
      } finally {
        await renewal.stop();
      }
    })();

    await pending.complete(flow.intent.intentId, ownerId);
    clearSubmissionRetryTimer();
    state.contribution = result.contribution;
    state.contributionStatus = "ready";
    state.authFlow = null;
    state.view = "list";
    flowReturnFocus = null;
    replaceSelected(null);
    await Promise.all([
      refreshContribution(session),
      loadLeaderboard(state.query),
    ]);
    announce(
      result.outcome === "created"
        ? "Your permanent contribution was cast."
        : `Your permanent contribution remains ${result.contribution.item.name}.`,
    );
  } catch (error) {
    clearSubmissionRetryTimer();
    await pending.releaseSubmissionLease(flow.intent.intentId, ownerId);
    await pending.markRetryable(flow.intent.intentId, contributionFailureCode(error));
    if (state.authFlow?.intent.intentId !== flow.intent.intentId) return;
    state.authFlow.status = "retryable_error";
    state.authFlow.message = contributionFailureMessage(error);
    state.view = "auth_status";
    render();
  }
}

function contributionFailureCode(error: unknown): string {
  return error instanceof ApiRequestError ? error.code : "ambiguous_failure";
}

function contributionFailureMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 401) return "Your sign-in is no longer active. Log out, then sign in again.";
    if (error.status === 429) return "Too many attempts. Wait briefly, then try this exact contribution again.";
    if (error.status === 404) return "That item is no longer available. Cancel this contribution to choose again.";
    if (error.status === 409) return "This contribution cannot be safely retried. Cancel it and start again.";
  }
  return "The contribution could not be confirmed. Try this exact contribution again.";
}

async function cancelPendingIntent(): Promise<void> {
  interactionGeneration += 1;
  const intentId = state.authFlow?.intent.intentId;
  if (intentId) await pending.cancel(intentId);
  clearSubmissionRetryTimer();
  state.authFlow = null;
  resetToList();
  announce("The unfinished contribution was canceled.");
}

async function logout(): Promise<void> {
  interactionGeneration += 1;
  clearSubmissionRetryTimer();
  clearCreateDraft();
  await pending.clearUnfinished();
  window.localStorage.removeItem(CALLBACK_ATTEMPT_STORAGE_KEY);
  window.localStorage.removeItem(STANDALONE_LOGIN_STORAGE_KEY);
  discardLegacyContribution();
  if (authClient) await authClient.auth.signOut({ scope: "local" });
  state.session = null;
  state.contribution = null;
  state.contributionStatus = "ready";
  state.contributionError = null;
  state.authFlow = null;
  state.loginError = null;
  flowReturnFocus = null;
  replaceSelected(null);
  state.view = "list";
  render();
  announce("You are logged out.");
}

function selectedFromIntent(intent: PendingContribution): SelectedItem {
  if (intent.target.kind === "existing") {
    return {
      kind: "existing",
      id: intent.target.itemId,
      name: intent.target.name,
      imageUrl: intent.target.imageUrl,
    };
  }
  return {
    kind: "created",
    name: intent.target.name,
    image: URL.createObjectURL(intent.target.image),
    imageBlob: intent.target.image,
  };
}

function requiredCreatedImage(item: Extract<SelectedItem, { kind: "created" }>): Blob {
  return item.imageBlob;
}

function resetToList(): void {
  interactionGeneration += 1;
  closeSuggestions(true);
  clearCreateDraft();
  state.loginError = null;
  state.view = "list";
  replaceSelected(null);
  state.authFlow = null;
  const returnFocus = flowReturnFocus;
  flowReturnFocus = null;
  render();
  queueMicrotask(() => {
    if (returnFocus?.isConnected) returnFocus.focus();
    else addItemButton.focus();
  });
}

function validateImage(file: File): void {
  if (!(SUPPORTED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    throw new Error("Use a JPG, PNG or WebP image.");
  }
  if (file.size > MAX_IMAGE_BYTES) throw new Error("Keep the image under 5 MB.");
}

function compressImage(file: File): Promise<{ blob: Blob; previewUrl: string }> {
  return new Promise((resolve, reject) => {
    const sourceUrl = URL.createObjectURL(file);
    const source = new Image();
    source.onerror = () => {
      URL.revokeObjectURL(sourceUrl);
      reject(new Error("That image could not be opened."));
    };
    source.onload = () => {
      URL.revokeObjectURL(sourceUrl);
      const scale = Math.min(1, 960 / source.width, 720 / source.height);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(source.width * scale));
      canvas.height = Math.max(1, Math.round(source.height * scale));
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) return reject(new Error("That image could not be prepared."));
      context.fillStyle = "oklch(0.07 0 0)";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(source, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("That image could not be prepared."));
          resolve({ blob, previewUrl: URL.createObjectURL(blob) });
        },
        "image/jpeg",
        0.78,
      );
    };
    source.src = sourceUrl;
  });
}

function replaceSelected(next: SelectedItem | null): void {
  const previousUrl = state.selected?.kind === "created" ? state.selected.image : null;
  const nextUrl = next?.kind === "created" ? next.image : null;
  if (previousUrl?.startsWith("blob:") && previousUrl !== nextUrl) {
    URL.revokeObjectURL(previousUrl);
  }
  state.selected = next;
}

function clearCreateDraft(): void {
  createImageSelectionGeneration += 1;
  const previewUrl = state.createDraft.preparedImage?.previewUrl;
  if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
  state.createDraft = { name: null, preparedImage: null };
}

function tabOwnerId(): string {
  const existing = window.sessionStorage.getItem(tabOwnerKey);
  if (existing) return existing;
  const ownerId = crypto.randomUUID();
  window.sessionStorage.setItem(tabOwnerKey, ownerId);
  return ownerId;
}

function discardLegacyContribution(): void {
  try {
    removeLegacyContribution(window.localStorage);
  } catch (_error) {
    // Storage can be unavailable in hardened browser contexts; it is never authoritative.
  }
}

function textButton(label: string, handler: () => void): HTMLButtonElement {
  const button = element("button", "text-action", label);
  button.type = "button";
  button.addEventListener("click", handler);
  return button;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className = "",
  text = "",
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function announce(message: string): void {
  announcer.textContent = "";
  window.setTimeout(() => {
    announcer.textContent = message;
  }, 50);
}

function ensureAddItemButton(): HTMLButtonElement {
  const existing = document.querySelector<HTMLButtonElement>("#add-item-button");
  if (existing) return existing;
  const button = element("button", "button button--primary add-item-button", "Add item");
  button.id = "add-item-button";
  button.type = "button";
  button.setAttribute("aria-haspopup", "dialog");
  button.setAttribute("aria-controls", "flow-dialog");
  searchForm.insertAdjacentElement("afterend", button);
  return button;
}

function ensureFlowDialog(): HTMLDialogElement {
  const existing = document.querySelector<HTMLDialogElement>("#flow-dialog");
  if (existing) return existing;
  const dialog = element("dialog", "flow-dialog");
  dialog.id = "flow-dialog";
  dialog.setAttribute("aria-labelledby", "flow-title");
  document.body.append(dialog);
  return dialog;
}

function queryRequired<T extends Element>(selector: string): T {
  const node = document.querySelector<T>(selector);
  if (!node) throw new Error(`Required element not found: ${selector}`);
  return node;
}
